import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { getPBPalette, stripHtml, getReadingTime, PB } from "../utils/pbBlogTheme";
import { FiClock, FiSearch, FiArrowUpRight, FiFilm } from "react-icons/fi";
import Nav from '../components/nav';
const BASE_URL = "https://employeeserverpilo.onrender.com";
const SITE_TARGET = "pb";

// ---------------------------------------------------------------------------
// Bouncing-dots loader
// ---------------------------------------------------------------------------
const DotsLoader = ({ label = "Loading stories" }) => (
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14, padding: "60px 20px" }}>
    <div style={{ display: "flex", gap: 8 }}>
      {[0, 1, 2].map(i => (
        <span key={i} style={{
          width: 11, height: 11, borderRadius: "50%",
          background: i === 0 ? PB.gold : i === 1 ? PB.violet : "#fb7185",
          animation: `pbBounce 1.2s ${i * 0.15}s infinite ease-in-out`,
        }} />
      ))}
    </div>
    <span style={{ fontSize: 13, color: PB.textDim, letterSpacing: "0.04em" }}>{label}</span>
    <style>{`@keyframes pbBounce{0%,80%,100%{transform:translateY(0);opacity:.4}40%{transform:translateY(-12px);opacity:1}}`}</style>
  </div>
);

// ---------------------------------------------------------------------------
// Scroll-reveal
// ---------------------------------------------------------------------------
const Reveal = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVis(true); o.disconnect(); } }, { threshold: 0.12 });
    if (ref.current) o.observe(ref.current);
    return () => o.disconnect();
  }, []);
  return (
    <div ref={ref} style={{
      opacity: vis ? 1 : 0,
      transform: vis ? "translateY(0)" : "translateY(30px)",
      transition: `opacity .7s cubic-bezier(.16,1,.3,1) ${delay}ms, transform .7s cubic-bezier(.16,1,.3,1) ${delay}ms`,
    }}>{children}</div>
  );
};

// ---------------------------------------------------------------------------
// Ambient drifting spotlight (signature element) — sits behind hero
// ---------------------------------------------------------------------------
const Spotlight = () => (
  <div aria-hidden="true" style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}>
    <div style={{
      position: "absolute", top: "-30%", left: "20%", width: 520, height: 520, borderRadius: "50%",
      background: `radial-gradient(circle, ${PB.gold}22 0%, transparent 70%)`,
      filter: "blur(20px)", animation: "pbDrift1 18s ease-in-out infinite",
    }} />
    <div style={{
      position: "absolute", top: "-10%", right: "10%", width: 460, height: 460, borderRadius: "50%",
      background: `radial-gradient(circle, ${PB.violet}22 0%, transparent 70%)`,
      filter: "blur(20px)", animation: "pbDrift2 22s ease-in-out infinite",
    }} />
    <style>{`
      @keyframes pbDrift1{0%,100%{transform:translate(0,0)}50%{transform:translate(60px,40px)}}
      @keyframes pbDrift2{0%,100%{transform:translate(0,0)}50%{transform:translate(-50px,30px)}}
    `}</style>
  </div>
);

// ---------------------------------------------------------------------------
// Story card
// ---------------------------------------------------------------------------
const StoryCard = ({ post, index }) => {
  const pal = getPBPalette(post.id);
  const mins = getReadingTime(post.content || post.excerpt || "");
  const cover = post.cover_image_id ? `${BASE_URL}/api/v1/blog/image/${post.cover_image_id}` : null;

  return (
    <Reveal delay={(index % 6) * 70}>
      <Link
        to={`/news/${post.slug}`}
        style={{ display: "block", height: "100%", color: "inherit", textDecoration: "none" }}
      >
      <article
        style={{
          position: "relative", borderRadius: 16, overflow: "hidden", cursor: "pointer",
          background: PB.bgRaised, border: `1px solid ${PB.border}`,
          display: "flex", flexDirection: "column", height: "100%",
          transition: "transform .3s cubic-bezier(.16,1,.3,1), box-shadow .3s, border-color .3s",
        }}
        onMouseEnter={e => {
          e.currentTarget.style.transform = "translateY(-6px)";
          e.currentTarget.style.boxShadow = `0 16px 50px ${pal.glow}`;
          e.currentTarget.style.borderColor = pal.accent;
        }}
        onMouseLeave={e => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.borderColor = PB.border;
        }}
      >
        {/* Visual */}
        <div style={{
          position: "relative", height: cover ? 190 : 130, overflow: "hidden",
          background: cover ? `center/cover url(${cover})` : PB.bgInput,
        }}>
          {!cover && (
            <div style={{
              position: "absolute", inset: 0,
              background: `radial-gradient(circle at 30% 30%, ${pal.glow}, transparent 65%)`,
            }} />
          )}
          {!cover && (
            <FiFilm size={30} style={{
              position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)",
              color: pal.accent, opacity: 0.5,
            }} />
          )}
          {/* accent top line */}
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: pal.grad }} />
          {post.tags?.[0] && (
            <span style={{
              position: "absolute", bottom: 12, left: 14,
              background: "rgba(0,0,0,0.6)", backdropFilter: "blur(8px)",
              color: pal.accent, fontSize: 10, fontWeight: 700, padding: "4px 11px",
              borderRadius: 6, textTransform: "uppercase", letterSpacing: "0.08em",
              border: `1px solid ${pal.accent}44`,
            }}>{post.tags[0]}</span>
          )}
        </div>

        {/* Body */}
        <div style={{ padding: "18px 20px 20px", display: "flex", flexDirection: "column", flex: 1 }}>
          <h3 style={{
            margin: "0 0 10px", fontSize: 19, fontWeight: 800, lineHeight: 1.28,
            color: PB.text, fontFamily: "'Georgia',serif", letterSpacing: "-0.01em",
          }}>{post.title}</h3>
          <p style={{
            margin: "0 0 18px", fontSize: 13.5, color: PB.textDim, lineHeight: 1.65, flex: 1,
            display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden",
          }}>{stripHtml(post.excerpt)}</p>

          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            paddingTop: 14, borderTop: `1px solid ${PB.border}`,
          }}>
            <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: PB.textDim }}>
              <FiClock size={12} /> {mins} min read
            </span>
            <span style={{
              display: "flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 700, color: pal.accent,
            }}>
              Read <FiArrowUpRight size={14} />
            </span>
          </div>
        </div>
      </article>
      </Link>
    </Reveal>
  );
};

// ---------------------------------------------------------------------------
// Skeleton card (shown while parallel fetch resolves)
// ---------------------------------------------------------------------------
const SkeletonCard = () => (
  <div style={{ borderRadius: 16, overflow: "hidden", background: PB.bgRaised, border: `1px solid ${PB.border}` }}>
    <div style={{ height: 150, background: `linear-gradient(90deg,${PB.bgInput},#22222e,${PB.bgInput})`, backgroundSize: "200% 100%", animation: "pbShim 1.4s infinite" }} />
    <div style={{ padding: 20 }}>
      <div style={{ height: 18, width: "75%", background: PB.bgInput, borderRadius: 4, marginBottom: 12 }} />
      <div style={{ height: 12, width: "100%", background: PB.bgInput, borderRadius: 4, marginBottom: 7 }} />
      <div style={{ height: 12, width: "55%", background: PB.bgInput, borderRadius: 4 }} />
    </div>
    <style>{`@keyframes pbShim{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════
// MAIN — PBNewsGrid
// ═══════════════════════════════════════════════════════════════════════════
const PBNewsGrid = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [search, setSearch] = useState("");
  const [activeTag, setActiveTag] = useState(null);
  const [tags, setTags] = useState([]);
  const loaderRef = useRef(null);

  const fetchPosts = useCallback(async (pageNum, reset = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        target: SITE_TARGET, page: pageNum, page_size: 9,
        ...(search && { search }), ...(activeTag && { tag: activeTag }),
      });
      const res = await fetch(`${BASE_URL}/api/v1/blog?${params}`);
      const data = await res.json();
      setPosts(prev => reset ? data.blogs : [...prev, ...data.blogs]);
      setHasMore(data.has_next);
    } catch { /* show empty state */ }
    finally { setLoading(false); }
  }, [search, activeTag]);

  const fetchTags = useCallback(async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/v1/blog/tags`);
      const data = await res.json();
      setTags(data.slice(0, 8));
    } catch { /* ignore */ }
  }, []);

  // Parallel initial load — posts + tags fire together, neither blocks the other
  useEffect(() => { setPage(1); fetchPosts(1, true); }, [search, activeTag, fetchPosts]);
  useEffect(() => { fetchTags(); }, [fetchTags]);

  useEffect(() => {
    const o = new IntersectionObserver(es => {
      if (es[0].isIntersecting && hasMore && !loading) {
        setPage(p => { const n = p + 1; fetchPosts(n); return n; });
      }
    }, { rootMargin: "240px" });
    if (loaderRef.current) o.observe(loaderRef.current);
    return () => o.disconnect();
  }, [hasMore, loading, fetchPosts]);

  const isFirstLoad = loading && posts.length === 0;

  return (
    <>
    <Nav />
    <div style={{
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      background: PB.bg, minHeight: "100vh", color: PB.text,
    }}>
      {/* Hero */}
      <div style={{ position: "relative", padding: "70px 20px 44px", textAlign: "center", overflow: "hidden" }}>
        <Spotlight />
        <div style={{ position: "relative" }}>
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700,
            color: PB.gold, letterSpacing: "0.16em", textTransform: "uppercase", marginBottom: 14,
          }}>
            <FiFilm size={13} /> ProducerBuddy · The Reel
          </span>
          <h1 style={{
            margin: "0 0 14px", fontSize: "clamp(32px,6vw,60px)", fontWeight: 900,
            fontFamily: "'Georgia',serif", lineHeight: 1.05, letterSpacing: "-0.02em",
            color: PB.text,
          }}>
            Stories from the<br />
            <span style={{
              background: `linear-gradient(135deg,${PB.gold},${PB.violet})`,
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text",
            }}>cutting room floor</span>
          </h1>
          <p style={{ maxWidth: 520, margin: "0 auto", fontSize: 15.5, color: PB.textDim, lineHeight: 1.6 }}>
            Insights, launches, and the craft of modern film production — straight from the team building ProducerBuddy.
          </p>
        </div>
      </div>

      {/* Search + tags */}
      <div style={{
        maxWidth: 920, margin: "0 auto 36px", padding: "0 20px",
        display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", justifyContent: "center",
      }}>
        <div style={{
          display: "flex", alignItems: "center", gap: 8, background: PB.bgRaised,
          borderRadius: 12, padding: "9px 16px", border: `1px solid ${PB.border}`, minWidth: 230,
        }}>
          <FiSearch size={14} style={{ color: PB.textDim }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search the reel..."
            style={{ border: "none", outline: "none", background: "transparent", fontSize: 13, color: PB.text, flex: 1 }} />
        </div>
        {tags.map(t => (
          <button key={t.slug} onClick={() => setActiveTag(activeTag === t.slug ? null : t.slug)}
            style={{
              padding: "8px 16px", borderRadius: 10, fontSize: 12, fontWeight: 600, cursor: "pointer",
              transition: "all .2s",
              border: `1px solid ${activeTag === t.slug ? PB.gold : PB.border}`,
              background: activeTag === t.slug ? `${PB.gold}1a` : PB.bgRaised,
              color: activeTag === t.slug ? PB.gold : PB.textDim,
            }}>#{t.name}</button>
        ))}
      </div>

      {/* Grid */}
      <div style={{
        maxWidth: 1180, margin: "0 auto", padding: "0 20px",
        display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(290px, 1fr))", gap: 22,
      }}>
        {posts.map((p, i) => <StoryCard key={p.id} post={p} index={i} />)}
        {loading && !isFirstLoad && Array.from({ length: 3 }).map((_, i) => <SkeletonCard key={`sk-${i}`} />)}
      </div>

      {/* First-load bouncing dots */}
      {isFirstLoad && <DotsLoader />}

      {!loading && posts.length === 0 && (
        <div style={{ textAlign: "center", padding: "60px 20px", color: PB.textDim }}>
          <FiFilm size={32} style={{ opacity: 0.4, marginBottom: 12 }} />
          <p style={{ fontSize: 15 }}>No stories yet{search || activeTag ? " for this filter" : ""}. Check back soon.</p>
        </div>
      )}

      <div ref={loaderRef} style={{ height: 1 }} />
      <div style={{ height: 60 }} />
    </div>
    </>
  );
};

export default PBNewsGrid;